/**
 * userSort.js — column sorting for the Users & access tables.
 *
 * Kept DOM-free (a .js module, not .jsx) so `node --test` can exercise it: the
 * comparison rules carry the real decisions, and those are what deserve tests.
 */

/**
 * Sorting for the Users & access tables.
 *
 * Values are pulled through the same maps the cells render from, so what you
 * sort by is what you see — a status column that reads "1 waiting · 3 approved"
 * sorts by what is WAITING, because that is the reason anyone sorts it.
 * A null/absent value always sorts last, whichever direction, so empty rows do
 * not crowd the top of a descending sort.
 */
export function sortRows(rows, sort, ctx = {}) {
  if (!sort?.key) return rows;
  const { pinByEmail = {}, roleByEmail = {}, isMailboxByEmail = {}, clinicStaff = null } = ctx;
  const email = (r) => (r.email || '').toLowerCase();
  const value = (r) => {
    switch (sort.key) {
      case 'email':      return email(r);
      case 'name':       return (r.displayName || '').toLowerCase();
      case 'role':       return String(roleByEmail[email(r)] || r.roleLabel || r.role || '').toLowerCase();
      case 'lastUnlock': return r.lastUnlockedAt ? Date.parse(r.lastUnlockedAt) : null;
      case 'pin':        return pinByEmail[email(r)] ? 1 : 0;
      case 'status': {
        // Clinic rows: sort by people waiting, then approved. Everyone else:
        // PIN set (1) or not (0), so the two kinds never interleave oddly.
        if (isMailboxByEmail[email(r)]) {
          const c = (clinicStaff || {})[email(r)];
          return 1000 + (c?.pending || 0) * 10 + (c?.active || 0);
        }
        return pinByEmail[email(r)] ? 1 : 0;
      }
      default:           return null;
    }
  };
  const dir = sort.dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const va = value(a), vb = value(b);
    const na = va === null || va === undefined || va === '';
    const nb = vb === null || vb === undefined || vb === '';
    if (na && nb) return 0;
    if (na) return 1;            // blanks last, in both directions
    if (nb) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb)) * dir;
  });
}


/**
 * Rows for Users & access ▸ All users: PIN holders, clinic mailboxes without a
 * PIN, and everyone else with access — members of role-granting Entra groups
 * and direct assignments — who has no PIN yet. Every role needs a PIN and the
 * sign-in event is only logged past the PIN screen, so those rows are marked
 * `neverSignedIn`. `signIn` narrows to 'in' (signed in) or 'never'; 'all' keeps
 * both. Emails compare lowercased; output is alphabetical by email.
 */
export function buildAllUserRows({ pinRows = [], mailboxRows = [], inheritedByEmail = {}, directUsers = [], signIn = 'all' }) {
  const lc = (e) => String(e || '').toLowerCase();
  const known = new Set([...pinRows, ...mailboxRows].map(r => lc(r.email)));
  const neverRows = [...new Set([
    ...Object.keys(inheritedByEmail).map(lc),
    ...directUsers.filter(u => u.email && u.principalType !== 'Group').map(u => lc(u.email)),
  ])]
    .filter(e => e && !known.has(e))
    .map(e => ({ email: e, neverSignedIn: true }));
  return [...pinRows, ...mailboxRows, ...neverRows]
    .filter(r => signIn === 'all' || (signIn === 'never') === !!r.neverSignedIn)
    .sort((a, b) => lc(a.email).localeCompare(lc(b.email)));
}

/**
 * Users & access search: every word typed must appear in the name or the email,
 * in any order — "eve sy" finds "Eve Sydenham <eves@…>", "sydenham" and "eves@"
 * do too. Blank query matches everything.
 */
export function matchesUserQuery({ email, name } = {}, query) {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = `${String(name || '').toLowerCase()} ${String(email || '').toLowerCase()}`;
  return words.every(w => hay.includes(w));
}
